<template>
  <div
    v-if="isResumeExist"
    class="mt-3 mw-456x mx-auto alert alert-danger text-center position-relative"
    role="alert"
  >
    プロファイルを作成してください！
    <button
      type="button"
      class="btn-close position-absolute end-0 pe-3 pt-2"
      data-bs-dismiss="alert"
      aria-label="Close"
    ></button>
  </div>

  <div
    v-if="showAlertUpdateProfile"
    class="alert alert-danger alert-dismissible fade show mw-456x mx-auto text-center"
    role="alert"
  >
    「SE転職ナビ　ダイレクト」をご利用いただくため、
    <br />
    まずはプロフィールをご登録ください。
    <button
      type="button"
      class="btn-close position-center-right"
      data-bs-dismiss="alert"
      aria-label="Close"
    ></button>
  </div>

  <Loading />

  <div
    id="loadingModal"
    class="modal fade"
    tabindex="-1"
    aria-hidden="true"
    data-bs-backdrop="static"
    data-bs-keyboard="false"
  >
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-body text-center p-4">
          <div class="spinner-border text-primary mb-3" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
          <p id="loadingModalMessage" class="mb-0 fs-5">{{ modalMessage }}</p>
        </div>
      </div>
    </div>
  </div>

  <form
    v-if="!isLoadingData"
    class="mw-68 container container-primary my-15 c-edit-profile"
    @submit="onSubmitForm"
    @keydown="preventSubmitOnEnter"
  >
    <BasicInfo />
    <WorkHistory />
    <DesiredCondition @uploadFile="uploadResume" @uploadFileProfessional="uploadFileProfessional" />

    <div
      class="px-5 mt-4 row justify-content-center text-center position-sticky py-3 z-index-sticky-button bottom-0 submit-button-container"
      :class="{ 'is-sticky': isSticky }"
    >
      <button
        class="col-12 lh-14 mw-280x btn btn-primary py-3 size-14 px-0 pb-11x pt-11x"
        type="submit"
        :disabled="isSubmitForm"
      >
        <span>登録する</span>
      </button>
    </div>
  </form>
  <ScrollToTopButton custom-class="position-right-unique mb-2d5" />
</template>

<script setup lang="ts">
import { onMounted, computed, ref, watch, nextTick, onUnmounted } from 'vue';
import * as yup from 'yup';
import { storeToRefs } from 'pinia';
import { useForm } from 'vee-validate';
import BasicInfo from '@/components/EditProfile/BasicInfo/Index.vue';
import WorkHistory from '@/components/EditProfile/WorkHistory/Index.vue';
import DesiredCondition from '@/components/EditProfile/DesiredCondition/Index.vue';
import { CandidateResumeType } from '@/types/pages/Profile/CandidateInformation';
import { getProfile, updateProfile } from '@/apis/candidate';
import { useAdditionalStore } from '@/stores/resume-candidate/Additional.store';
import { useBaseInformationStore } from '@/stores/resume-candidate/BasicInformation.store';
import { useResumeEducationStore } from '@/stores/resume-candidate/ResumeEducation.store';
import { useWorkHistoryStore } from '@/stores/resume-candidate/WorkHistory.store';
import { useOptionalInformationStore } from '@/stores/resume-candidate/OptionalInformation.store';
import { ResumeEducation } from '@/types/pages/ResumeEducation';
import { BasicInformationPrimary } from '@/types/pages/BasicInformation';
import { useSkillStore } from '@/stores/resume-candidate/Skill.store';
import { useJobHistoryStore } from '@/stores/resume-candidate/JobHistory.store';
import { useFormErrorsStore } from '@/stores/resume-candidate/Errors.store';
import { ZIP_CODE_REGEX } from '@/constant';
import { HIRAGANA_KATAKANA_REGEX, PLATFORM_CODE, URL_REGEX } from '@/utils/constants';
import { scrollToFirstErrorEl } from '@/utils/errorHandle';
import { useMessageStore } from '@/stores/messages.store';
import { ROOT_PATH } from '@/constant/routers/path';
import ScrollToTopButton from '@/components/ScrollToTopButton.vue';
import Loading from '@/components/Loading/index.vue';
import { useFormLeaveConfirmation } from '@/hooks/useFormLeaveConfirmation';

const additionalStore = useAdditionalStore();
const baseInformationStore = useBaseInformationStore();
const resumeEducation = useResumeEducationStore();
const workHistory = useWorkHistoryStore();
const optionalInformation = useOptionalInformationStore();
const skillStore = useSkillStore();
const jobHistory = useJobHistoryStore();
const messageStore = useMessageStore();
const formErrorsStore = useFormErrorsStore();

// フォームデータを統合
const formData = computed(() => ({
  // 基本情報
  first_name: baseInformationStore.first_name,
  last_name: baseInformationStore.last_name,
  first_name_kana: baseInformationStore.first_name_kana,
  last_name_kana: baseInformationStore.last_name_kana,
  birth_year: baseInformationStore.birth_year,
  birth_month: baseInformationStore.birth_month,
  birth_day: baseInformationStore.birth_day,
  gender: baseInformationStore.gender,
  platform: baseInformationStore.platform,
  profile_resume_occupations: baseInformationStore.profile_resume_occupations,
  profile_resume_desired_locations: baseInformationStore.profile_resume_desired_locations,

  // 追加情報
  zip_code: additionalStore.zip_code,
  prefecture_id: additionalStore.prefecture_id,
  address: additionalStore.address,
  tel: additionalStore.tel,
  is_married: additionalStore.is_married,
  willingness: additionalStore.willingness,
  city_id: additionalStore.city_id,

  // 学歴
  school_category_id: resumeEducation.school_category_id,
  school_name: resumeEducation.school_name,
  faculty: resumeEducation.faculty,
  graduated_year: resumeEducation.graduated_year,
  graduated_month: resumeEducation.graduated_month,
  graduation_classification_id: resumeEducation.graduation_classification_id,
  other_educational_backgrounds: resumeEducation.other_educational_backgrounds,

  // 職歴
  income_id: workHistory.income_id,
  experienced_companies_number: workHistory.experienced_companies_number,
  experienced_companies_remarks: workHistory.experienced_companies_remarks,
  years_of_service: workHistory.years_of_service,
  from_year: workHistory.from_year,
  from_month: workHistory.from_month,
  to_year: workHistory.to_year,
  to_month: workHistory.to_month,
  is_enrolled: workHistory.is_enrolled,
  employment_type_id: workHistory.employment_type_id,
  corporation_name: workHistory.corporation_name,
  candidate_resume_work_histories: workHistory.candidate_resume_work_histories,

  // 追加情報
  native_language_id: optionalInformation.native_language_id,
  language_remarks: optionalInformation.language_remarks,
  desired_annual_income: optionalInformation.desired_annual_income,
  qualifications_description: optionalInformation.qualifications_description,
  special_remarks: optionalInformation.special_remarks,
  desired_occupations: optionalInformation.desired_occupations,
  candidate_resume_industries: optionalInformation.candidate_resume_industries,
  profile_desired_industires: optionalInformation.profile_desired_industires,
  resume_languages: optionalInformation.resume_languages,

  // スキル
  self_description: skillStore.self_description,
  portfolio_url: skillStore.portfolio_url,
  candidate_resume_skills: skillStore.candidate_resume_skills,

  // 職務経歴
  work_experience_description: jobHistory.work_experience_description,
}));

// フォーム離脱確認機能
const { setupFormLeaveConfirmation, saveInitialData, resetFormChanged } =
  useFormLeaveConfirmation(formData);
const cleanupBeforeUnload = ref<(() => void) | null>(null);

const fileResume = ref<File>();
const fileProfessionalResume = ref<File>();
const isSubmitForm = ref(false);
const redirectUrl = ref();
const modalMessage = ref('処理中です...');

const genderType = Object.freeze({
  OTHER: 'other',
});
const isResumeExist = ref(false);
const showAlertUpdateProfile = ref(false);
const isLoadingData = ref(true);

const {
  first_name,
  last_name,
  first_name_kana,
  last_name_kana,
  birth_day,
  birth_month,
  birth_year,
  gender,
  profile_resume_desired_locations,
  profile_resume_occupations,
  platform,
} = storeToRefs(baseInformationStore);

const { zip_code, prefecture_id, address, tel, is_married, willingness, city_id } =
  storeToRefs(additionalStore);

const {
  school_category_id,
  school_name,
  faculty,
  graduated_year,
  graduated_month,
  graduation_classification_id,
  other_educational_backgrounds,
} = storeToRefs(resumeEducation);

const { income_id, experienced_companies_number, experienced_companies_remarks } =
  storeToRefs(workHistory);

const {
  native_language_id,
  language_remarks,
  desired_annual_income,
  qualifications_description,
  special_remarks,
  desired_occupations,
} = storeToRefs(optionalInformation);

const { self_description, portfolio_url, candidate_resume_skills } = storeToRefs(skillStore);

const { work_experience_description } = storeToRefs(jobHistory);

const schema = computed(() =>
  yup
    .object({
      first_name: yup
        .string()
        .required('氏名（漢字）は必須項目です')
        .max(50, '氏名（漢字）は50文字以内でなければなりません'),
      last_name: yup
        .string()
        .required('氏名（漢字）は必須項目です')
        .max(50, '氏名（漢字）は50文字以内でなければなりません'),
      first_name_kana: yup
        .string()
        .required('氏名（カナ）は必須項目です')
        .max(50, '氏名（カナ）は50文字以内でなければなりません')
        .matches(HIRAGANA_KATAKANA_REGEX, '氏名（カナ）はひらがなまたはカタカナで入力してください'),
      last_name_kana: yup
        .string()
        .required('氏名（カナ）は必須項目です')
        .max(50, '氏名（カナ）は50文字以内でなければなりません')
        .matches(HIRAGANA_KATAKANA_REGEX, '氏名（カナ）はひらがなまたはカタカナで入力してください'),
      birth_year: yup.string().required('年は必須項目です'),
      birth_month: yup.string().required('月は必須項目です'),
      birth_day: yup.string().required('日は必須項目です'),
      zip_code: yup
        .string()
        .required('郵便番号必須項目です')
        .matches(ZIP_CODE_REGEX, '郵便番号の形式が正しくありません'),
      prefecture_id: yup
        .string()
        .test(
          'prefecture_id',
          '現住所（都道府県 市区町村）は必須項目です',
          () => !!additionalStore.prefecture_name && !!additionalStore.city_name
        ),
      address: yup
        .string()
        .required('現住所（町名-番地）は必須項目です')
        .max(100, '現住所（町名-番地）は100文字以内でなければなりません'),
      income_id: yup.string().required('現在（前職）の年収は必須項目です'),
      tel: yup
        .string()
        .required('電話番号は必須項目です')
        .test('numeric', '電話番号は半角数字で入力してください。', (value) =>
          /^[0-9]*$/.test(value)
        )
        .max(255, '電話番号は255文字以内でなければなりません')
        .matches(/^[0-9]*$/, '電話番号は半角数字で入力してください。'),
      corporation_name: yup.string().nullable(),
      employment_type_id: yup.string().nullable(),
      from_month: yup.string().nullable(),
      from_year: yup.string().nullable(),
      to_month: yup.string().nullable(),
      to_year: yup.string().nullable(),
      candidate_resume_work_histories: yup.array().of(
        yup.object().shape({
          corporation_name: yup
            .string()
            .required('企業名は必須項目です')
            .max(255, '企業名は255文字以内でなければなりません'),
          from_year: yup.string().required('年は必須項目です'),
          from_month: yup.string().required('月は必須項目です'),
          to_year: yup.string().required('年は必須項目です'),
          to_month: yup.string().required('月は必須項目です'),
          employment_type_id: yup.string().required('雇用形態は必須項目です'),
        })
      ),
      candidate_resume_skills: yup
        .array()
        .test(
          'candidate_resume_skills',
          'スキルは必須項目です',
          () => candidate_resume_skills.value.length > 0
        ),
      profile_resume_occupations: yup
        .array()
        .test('profile_resume_occupations', '経験職種は必須項目です', (value) => {
          return Array.isArray(value) && value.length > 0 && value[0].occupation_id !== 0;
        }),
      profile_resume_desired_locations: yup
        .array()
        .test(
          'profile_resume_desired_locations',
          '希望勤務地は必須項目です',
          () => profile_resume_desired_locations.value.length > 0
        ),
      desired_occupations: yup
        .array()
        .test('desired_occupations', '希望職種は必須項目です', (value) => {
          return Array.isArray(value) && value.length > 0 && value[0].occupation_id !== 0;
        }),
      platform: yup
        .array()
        .test('platform', '利用サービスは必須項目です', () => platform.value.length > 0),
      candidate_resume: yup.object({
        is_married: yup
          .string()
          .required('配偶者は必須項目です')
          .oneOf(['yes', 'no'], '配偶者は必須項目です'),
        school_category_id: yup
          .number()
          .required('最終学歴は必須項目です')
          .typeError('最終学歴は必須項目です'),
        school_name: yup
          .string()
          .required('学校名は必須項目です')
          .max(100, '学校名は100文字以内でなければなりません'),
        faculty: yup
          .string()
          .required('学部 / 学科は必須項目です')
          .max(100, '学部 / 学科は100文字以内でなければなりません'),
        graduated_year: yup.number().required('年は必須項目です').typeError('年は必須項目です'),
        graduated_month: yup.number().required('月は必須項目です').typeError('月は必須項目です'),
        graduation_classification_id: yup
          .number()
          .required('区分は必須項目です')
          .typeError('区分は必須項目です'),
        work_experience_description: yup
          .string()
          .required('職務経歴は必須項目です')
          .max(5000, '職務経歴は5000文字以内でなければなりません'),
      }),
    })
    .shape({
      portfolio_url: skillStore.portfolio_url
        ? yup
            .string()
            .matches(URL_REGEX, 'URLの形式が正しくありません')
            .max(200, 'ポートフォリオURLは200文字以内でなければなりません')
        : yup.string(),
      employment_type_id: yup.string().required('雇用形態は必須です'),
      corporation_name: yup
        .string()
        .required('企業名は必須です')
        .max(100, '企業名は100文字以内でなければなりません'),
      from_month: yup.string().required('月は必須です'),
      from_year: yup.string().required('年は必須です'),
      to_month: workHistory.is_enrolled
        ? yup.string().nullable()
        : yup.string().required('月は必須です'),
      to_year: workHistory.is_enrolled
        ? yup.string().nullable()
        : yup.string().required('年は必須です'),
      special_remarks: yup.string().max(1000, 'その他は1000文字以内でなければなりません'),
      language_remarks: yup.string().max(500, '語学備考欄は500文字以内でなければなりません'),
      self_description: yup.string().max(2000, '自己PRは2000文字以内でなければなりません'),
      qualifications_description: yup.string().max(500, '資格は500文字以内でなければなりません'),
      experienced_companies_remarks: yup
        .string()
        .max(2000, 'その他備考は2000文字以内でなければなりません'),
      other_educational_backgrounds: yup
        .string()
        .max(300, '学歴メモ（その他学歴）は300文字以内でなければなりません'),
      desired_annual_income: yup.string().max(300, '希望年収は300文字以内でなければなりません'),
    })
);

const { handleSubmit, submitCount, errors, setFieldValue, validate } = useForm({
  initialValues: {
    zip_code: '',
    prefecture_id: '',
    address: '',
    tel: '',
    first_name: '',
    last_name: '',
    first_name_kana: '',
    last_name_kana: '',
    birth_year: '',
    birth_month: '',
    birth_day: '',
    income_id: '',
    employment_type_id: '',
    corporation_name: '',
    from_month: '',
    from_year: '',
    to_month: '',
    to_year: '',
    profile_resume_occupations: profile_resume_occupations.value,
    desired_occupations: desired_occupations.value,
  },
  validationSchema: schema,
});

watch(errors, (newErrors) => {
  formErrorsStore.setErrors(newErrors || {});
});

watch(
  () => submitCount.value,
  async () => {
    await nextTick();
    setTimeout(() => {
      if (Object.keys(errors.value).length > 0) {
        scrollToFirstErrorEl(errors);
      }
    }, 100);
  }
);

watch(
  () => submitCount.value,
  async () => {
    await nextTick();
  }
);

const isSticky = ref(false);
let buttonContainer: HTMLElement | null = null;

const checkSticky = (): void => {
  if (!buttonContainer) return;

  const rect = buttonContainer.getBoundingClientRect();
  const distanceFromBottom = window.innerHeight - rect.top;

  // 画面下部との距離が要素の高さと同等の場合はtrue
  isSticky.value = distanceFromBottom === rect.height;
};

onMounted(async (): Promise<void> => {
  await getDataProfile();
  onFocusElement();
  setRedirectUrl();
  if (localStorage.getItem('form_validation')) {
    checkFormValidation();
  }

  buttonContainer = document.querySelector('.submit-button-container');
  if (buttonContainer) {
    // 初期状態をチェック
    checkSticky();
    // スクロールイベントを監視
    window.addEventListener('scroll', checkSticky);
  }

  // フォーム離脱確認機能を初期化（データ読み込み後）
  nextTick(() => {
    cleanupBeforeUnload.value = setupFormLeaveConfirmation();
  });
});

onUnmounted((): void => {
  // スクロールイベントリスナーを解除
  window.removeEventListener('scroll', checkSticky);

  // フォーム離脱確認機能のクリーンアップ
  if (cleanupBeforeUnload.value) {
    cleanupBeforeUnload.value();
  }
});

const setRedirectUrl = (): void => {
  redirectUrl.value = localStorage.getItem('redirect_url');
  if (redirectUrl.value) localStorage.removeItem('redirect_url');
};

const onFocusElement = (): void => {
  const hash = window.location.hash;
  if (hash) {
    const id = hash.substring(1);
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView(true); // Scroll to top element
    }
  }
};

const getDataProfile = async (): Promise<void> => {
  const response = await getProfile();
  if (response.status === 200) {
    isLoadingData.value = false;
    isResumeExist.value = response.data.candidate_resume ? false : true;
    showAlertUpdateProfile.value =
      response.data.can_apply_job === false && response.data.platform === PLATFORM_CODE.DIRECT;
    setBasicInformation(response.data);
    setAdditions(response.data.candidate_resume);
    setResumeEducation(response.data.candidate_resume);
    setWorkHistory(response.data);
    setOptionalInformation(response.data);
    setSkill(response.data);
    setJobHistory(response.data.candidate_resume);

    // フォームの初期値を保存
    nextTick(() => {
      saveInitialData();
    });
  }
};

const setAdditions = (data: CandidateResumeType): void => {
  additionalStore.zip_code = data?.zip_code || '';
  additionalStore.prefecture_id = parseInt(data?.prefecture_id ?? '') || undefined;
  additionalStore.address = data?.address || undefined;
  additionalStore.tel = data?.tel || undefined;
  additionalStore.is_married = data?.is_married || 'no';
  additionalStore.willingness = data?.willingness || 'have_chance';
  additionalStore.prefecture_name = data?.prefecture_name;
  additionalStore.city_name = data?.city_name;
  additionalStore.city_id = parseInt(data?.city_id ?? '') || undefined;
};

const setBasicInformation = (data: BasicInformationPrimary): void => {
  baseInformationStore.email = data?.email || undefined;
  baseInformationStore.first_name = data?.first_name || undefined;
  baseInformationStore.last_name = data?.last_name || undefined;
  baseInformationStore.first_name_kana = data?.first_name_kana || undefined;
  baseInformationStore.last_name_kana = data?.last_name_kana || undefined;
  baseInformationStore.gender = data?.gender || genderType.OTHER;
  baseInformationStore.birth_year = data?.birth_year || undefined;
  baseInformationStore.birth_month = data?.birth_month || undefined;
  baseInformationStore.birth_day = data?.birth_day || undefined;
  baseInformationStore.setCandidatePlatform(data?.platform);
  baseInformationStore.setResumeOccupations(
    data?.candidate_resume_occupations.length
      ? data?.candidate_resume_occupations
      : [{ occupation_id: 0, years_of_experience: 1 }]
  );
  baseInformationStore.setResumeDesiredLocations(data?.candidate_resume_desired_locations || []);
};

const setResumeEducation = (data: ResumeEducation): void => {
  resumeEducation.school_category_id = data?.school_category_id ?? undefined;
  resumeEducation.school_name = data?.school_name || undefined;
  resumeEducation.faculty = data?.faculty || undefined;
  resumeEducation.graduated_year = data?.graduated_year || undefined;
  resumeEducation.graduated_month = data?.graduated_month || undefined;
  resumeEducation.graduation_classification_id = data?.graduation_classification_id || 1;
  resumeEducation.other_educational_backgrounds = data?.other_educational_backgrounds || undefined;
};

const setWorkHistory = (data: any): void => {
  const firstIndex = 0;
  workHistory.id = data?.candidate_resume_work_histories[firstIndex]?.id || undefined;
  workHistory.income_id = data?.candidate_resume?.income_id || undefined;
  workHistory.experienced_companies_number =
    data?.candidate_resume?.experienced_companies_number || undefined;
  workHistory.experienced_companies_remarks =
    data?.candidate_resume?.experienced_companies_remarks || undefined;
  workHistory.years_of_service =
    data?.candidate_resume_work_histories[firstIndex]?.years_of_service || undefined;
  workHistory.from_year = data?.candidate_resume_work_histories[firstIndex]?.from_year || undefined;
  workHistory.from_month =
    data?.candidate_resume_work_histories[firstIndex]?.from_month || undefined;
  workHistory.to_year = data?.candidate_resume_work_histories[firstIndex]?.to_year || undefined;
  workHistory.to_month = data?.candidate_resume_work_histories[firstIndex]?.to_month || undefined;
  workHistory.is_enrolled = data?.candidate_resume_work_histories[firstIndex]?.is_enrolled ?? true;
  workHistory.employment_type_id =
    data?.candidate_resume_work_histories[firstIndex]?.employment_type_id || undefined;
  workHistory.corporation_name =
    data?.candidate_resume_work_histories[firstIndex]?.corporation_name || undefined;
  if (data?.candidate_resume_work_histories.length > 1) {
    workHistory.candidate_resume_work_histories = data?.candidate_resume_work_histories.slice(1);
  }
};

const setOptionalInformation = (data: any): void => {
  optionalInformation.native_language_id = data?.candidate_resume?.native_language_id || undefined;
  optionalInformation.setResumeIndustires(data?.candidate_resume_industries || []);
  optionalInformation.setDesiredIndustries(data?.candidate_resume_desired_industries || []);
  optionalInformation.setResumeLanguages(data?.candidate_resume_languages || []);
  optionalInformation.setCandidateDaxtraJsons(data?.candidate_daxtra_jsons || []);
  optionalInformation.desired_occupations = data?.candidate_resume_desired_occupations || [];
  optionalInformation.language_remarks = data?.candidate_resume?.language_remarks || undefined;
  optionalInformation.desired_annual_income =
    data?.candidate_resume?.desired_annual_income || undefined;
  optionalInformation.qualifications_description =
    data?.candidate_resume?.qualifications_description || undefined;
  optionalInformation.special_remarks = data?.candidate_resume?.special_remarks || undefined;
};

const setSkill = (data: any): void => {
  skillStore.setSkillExperience(data?.candidate_resume_skills || []);
  skillStore.self_description = data?.candidate_resume?.self_description || undefined;
  skillStore.portfolio_url = data?.candidate_resume?.portfolio_url || undefined;
};

const setJobHistory = (data: CandidateResumeType): void => {
  jobHistory.work_experience_description = data?.work_experience_description || undefined;
};

watch(
  [profile_resume_occupations, desired_occupations],
  ([newProfileOccupations, newDesiredOccupations]) => {
    setFieldValue('profile_resume_occupations', newProfileOccupations);
    setFieldValue('desired_occupations', newDesiredOccupations);
  },
  { deep: true }
);

const checkFormValidation = handleSubmit(null, (): void => {
  validate();
  localStorage.removeItem('form_validation');
});

const onSubmitForm = handleSubmit(async (): Promise<void> => {
  isSubmitForm.value = true;
  showLoadingModal();

  // フォーム送信時に変更フラグをリセット
  resetFormChanged();

  const birth_date = `${birth_year.value}-${birth_month.value}-${birth_day.value}`;
  const candidate_resume_work_histories = [];

  if (workHistory.corporation_name && workHistory.employment_type_id) {
    candidate_resume_work_histories.push({
      id: workHistory.id,
      years_of_service: workHistory.years_of_service,
      from_year: workHistory.from_year,
      from_month: workHistory.from_month,
      to_year: workHistory.to_year,
      to_month: workHistory.to_month,
      is_enrolled: workHistory.is_enrolled,
      employment_type_id: workHistory.employment_type_id,
      corporation_name: workHistory.corporation_name,
    });
  }

  if (workHistory.candidate_resume_work_histories) {
    workHistory.candidate_resume_work_histories.forEach((item) => {
      candidate_resume_work_histories.push({
        years_of_service: item.years_of_service,
        from_year: item.from_year,
        from_month: item.from_month,
        to_year: item.to_year,
        to_month: item.to_month,
        is_enrolled: false,
        employment_type_id: item.employment_type_id,
        corporation_name: item.corporation_name,
      });
    });
  }

  const formData = new FormData();
  formData.append('first_name', String(first_name.value));
  formData.append('last_name', String(last_name.value));
  formData.append('first_name_kana', String(first_name_kana.value));
  formData.append('last_name_kana', String(last_name_kana.value));
  formData.append('birth_date', birth_date);
  formData.append('gender', String(gender?.value));
  formData.append(
    'candidate_resume[experienced_companies_remarks]',
    String(experienced_companies_remarks.value || '')
  );
  formData.append('candidate_resume[zip_code]', String(zip_code?.value));
  formData.append('candidate_resume[prefecture_id]', String(prefecture_id.value));
  formData.append('candidate_resume[address]', String(address.value));
  formData.append('candidate_resume[city_id]', String(city_id.value));
  formData.append('candidate_resume[tel]', String(tel.value));
  formData.append('candidate_resume[is_married]', String(is_married.value));
  formData.append('candidate_resume[school_category_id]', String(school_category_id.value));
  formData.append('candidate_resume[school_name]', String(school_name.value || ''));
  formData.append('candidate_resume[faculty]', String(faculty.value || ''));
  formData.append('candidate_resume[graduated_year]', String(graduated_year.value));
  formData.append('candidate_resume[graduated_month]', String(graduated_month.value));
  formData.append(
    'candidate_resume[graduation_classification_id]',
    String(graduation_classification_id.value)
  );
  formData.append(
    'candidate_resume[other_educational_backgrounds]',
    String(other_educational_backgrounds.value || '')
  );
  formData.append('candidate_resume[language_remarks]', String(language_remarks.value || ''));
  formData.append('candidate_resume[income_id]', String(income_id?.value));
  formData.append('candidate_resume[willingness]', String(willingness.value));
  formData.append(
    'candidate_resume[qualifications_description]',
    String(qualifications_description.value || '')
  );
  formData.append('candidate_resume[special_remarks]', String(special_remarks.value || ''));
  formData.append(
    'candidate_resume[experienced_companies_number]',
    String(experienced_companies_number?.value || null)
  );
  formData.append('candidate_resume[self_description]', String(self_description.value || ''));
  formData.append('candidate_resume[portfolio_url]', String(portfolio_url.value || ''));
  formData.append(
    'candidate_resume[desired_annual_income]',
    String(desired_annual_income.value || '')
  );
  formData.append(
    'candidate_resume[work_experience_description]',
    String(work_experience_description?.value || '')
  );
  if (native_language_id.value !== undefined)
    formData.append('candidate_resume[native_language_id]', String(native_language_id.value));

  optionalInformation.profile_desired_industires
    .filter((industry) => industry.industry_id !== 0)
    .forEach((industry) => {
      formData.append('candidate_resume_desired_industries[][id]', String(industry.id || ''));
      formData.append(
        'candidate_resume_desired_industries[][industry_id]',
        String(industry.industry_id)
      );
    });

  optionalInformation.candidate_resume_industries
    .filter((industry) => industry.industry_id !== 0)
    .forEach((industry) => {
      formData.append('candidate_resume_industries[][id]', String(industry.id || ''));
      formData.append('candidate_resume_industries[][industry_id]', String(industry.industry_id));
      formData.append(
        'candidate_resume_industries[][years_of_experience]',
        String(industry.years_of_experience)
      );
    });

  optionalInformation.resume_languages
    .filter((language) => language.other_language_id !== 0)
    .forEach((language) => {
      formData.append('candidate_resume_languages[][id]', String(language.id || ''));
      formData.append(
        'candidate_resume_languages[][other_language_id]',
        String(language.other_language_id)
      );
      formData.append(
        'candidate_resume_languages[][other_language_level_id]',
        String(language.other_language_level_id)
      );
    });

  skillStore.candidate_resume_skills
    .filter((skill) => skill.skill_id !== 0)
    .forEach((skill) => {
      formData.append('candidate_resume_skills[][id]', String(skill.id || ''));
      formData.append('candidate_resume_skills[][skill_id]', String(skill.skill_id));
      formData.append(
        'candidate_resume_skills[][years_of_experience]',
        String(skill.years_of_experience)
      );
    });

  baseInformationStore.profile_resume_occupations
    .filter((occupation) => occupation.occupation_id !== 0)
    .forEach((occupation) => {
      formData.append('candidate_resume_occupations[][id]', String(occupation.id || ''));
      formData.append(
        'candidate_resume_occupations[][occupation_id]',
        String(occupation.occupation_id)
      );
      formData.append(
        'candidate_resume_occupations[][years_of_experience]',
        String(occupation.years_of_experience)
      );
    });

  optionalInformation.desired_occupations
    .filter((occupation) => occupation.occupation_id !== 0)
    .forEach((occupation) => {
      formData.append('candidate_resume_desired_occupations[][id]', String(occupation.id || ''));
      formData.append(
        'candidate_resume_desired_occupations[][occupation_id]',
        String(occupation.occupation_id)
      );
    });

  baseInformationStore.profile_resume_desired_locations
    .filter((location) => location.prefecture_id !== 0)
    .forEach((location) => {
      formData.append(
        'candidate_resume_desired_locations[][prefecture_id]',
        String(location.prefecture_id)
      );
      if (location.city_id)
        formData.append('candidate_resume_desired_locations[][city_id]', String(location.city_id));
    });

  candidate_resume_work_histories.forEach((workHistory) => {
    formData.append('candidate_resume_work_histories[][id]', String(workHistory.id || ''));
    formData.append(
      'candidate_resume_work_histories[][corporation_name]',
      String(workHistory.corporation_name)
    );
    formData.append('candidate_resume_work_histories[][from_year]', String(workHistory.from_year));
    formData.append(
      'candidate_resume_work_histories[][from_month]',
      String(workHistory.from_month)
    );
    formData.append('candidate_resume_work_histories[][to_year]', String(workHistory.to_year));
    formData.append('candidate_resume_work_histories[][to_month]', String(workHistory.to_month));
    formData.append(
      'candidate_resume_work_histories[][is_enrolled]',
      String(workHistory.is_enrolled)
    );
    formData.append(
      'candidate_resume_work_histories[][employment_type_id]',
      String(workHistory.employment_type_id)
    );
    formData.append(
      'candidate_resume_work_histories[][years_of_service]',
      String(workHistory.years_of_service)
    );
  });

  optionalInformation.candidate_daxtra_jsons.forEach((daxtra) => {
    formData.append('candidate_daxtra_jsons[][id]', String(daxtra.id));
    formData.append('candidate_daxtra_jsons[][name]', String(daxtra.name));
    formData.append('candidate_daxtra_jsons[][file_type]', String(daxtra.file_type));
  });

  if (fileResume.value) formData.append('file_resume', fileResume.value);
  if (fileProfessionalResume.value)
    formData.append('file_professional_resume', fileProfessionalResume.value);

  try {
    const response = await updateProfile(formData);
    if (response.status == 200) {
      window.location.href = redirectUrl.value || ROOT_PATH;
      if (redirectUrl.value) localStorage.setItem('interview_apply_modal', 'true');
    } else {
      messageStore.field_error = response.data.field_error;
      isSubmitForm.value = false;
      hideLoadingModal();
    }
  } catch (error) {
    console.error('送信に失敗しました', error);
    isSubmitForm.value = false;
    hideLoadingModal();
  }
});

const preventSubmitOnEnter = (event: any): void => {
  if (event.key === 'Enter' && event.target.tagName !== 'TEXTAREA') {
    event.preventDefault();
  }
};

const uploadResume = (file: File | null): void => {
  fileResume.value = file || undefined;
};

const uploadFileProfessional = (file: File | null): void => {
  fileProfessionalResume.value = file || undefined;
};

const showLoadingModal = (): void => {
  const modalElement = document.getElementById('loadingModal');
  if (!modalElement) return;

  modalElement.classList.add('show');
  modalElement.style.display = 'block';
  document.body.classList.add('modal-open');

  if (!document.querySelector('.modal-backdrop')) {
    const backdrop = document.createElement('div');
    backdrop.className = 'modal-backdrop fade show';
    document.body.appendChild(backdrop);
  }
};

const hideLoadingModal = (): void => {
  const modalElement = document.getElementById('loadingModal');
  if (!modalElement) return;

  modalElement.classList.remove('show');
  modalElement.style.display = 'none';
  document.body.classList.remove('modal-open');
  document.body.style.overflow = '';
  document.body.style.paddingRight = '';

  const backdrop = document.querySelector('.modal-backdrop');
  if (backdrop && backdrop.parentNode) {
    backdrop.parentNode.removeChild(backdrop);
  }
};
</script>

<style scoped lang="scss">
.submit-button-container {
  width: 100vw;
  left: 0;
  margin-left: calc(-50vw + 50%);
  background-color: transparent;
  transition: background-color 0.1s ease;

  &.is-sticky {
    background-color: rgba(0, 0, 0, 0.5);
    transition: background-color 0.3s ease;
  }
}
</style>
